import { Fragment } from "react"

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import {
  BookCopy,
  PanelLeft,
  Search,
  Shield,
  Star,
  User
} from "lucide-react"
import { Link } from "react-router-dom"
import { Logo } from "../icons"
import { useBreadcrumbContext } from "./breadcrumb-context"
import { UserNav } from "./UserNav"
import { NotificationBell } from "./NotificationBell"
import { useAuthProfile } from "@/hooks/use-auth-profile"

export function Header() {
  const { items } = useBreadcrumbContext()
  const { profile } = useAuthProfile();
  const isAdmin = profile?.isAdmin === true;

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-4 border-b bg-background px-4 sm:static sm:h-auto sm:border-0 sm:bg-transparent sm:px-6">
      <Sheet>
        <SheetTrigger asChild>
          <Button 
            size="icon" 
            variant="outline" 
            className="sm:hidden"
            suppressHydrationWarning
          >
            <PanelLeft className="h-5 w-5" />
            <span className="sr-only">Toggle Menu</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="sm:max-w-xs">
          <DialogTitle className="sr-only">Navigation Menu</DialogTitle>
          <nav className="grid gap-6 text-lg font-medium">
            <Link
              to="/home"
              className="group flex h-10 w-10 shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-lg font-semibold text-primary-foreground md:text-base"
            >
              <Logo className="h-5 w-5 transition-all group-hover:scale-110" />
              <span className="sr-only">ezRecipeBox</span>
            </Link>
            <Link
              to="/home"
              className="flex items-center gap-4 px-2.5 text-muted-foreground hover:text-foreground"
            >
              <BookCopy className="h-5 w-5" />
              Recipe Boxes
            </Link>
            <Link
              to="/favorites"
              className="flex items-center gap-4 px-2.5 text-muted-foreground hover:text-foreground"
            >
              <Star className="h-5 w-5" />
              Favorites
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="flex items-center gap-4 px-2.5 text-muted-foreground hover:text-foreground"
              >
                <Shield className="h-5 w-5" />
                Admin
              </Link>
            )}
            <Link
              to="/profile"
              className="flex items-center gap-4 px-2.5 text-muted-foreground hover:text-foreground"
            >
              <User className="h-5 w-5" />
              Profile
            </Link>
          </nav>
        </SheetContent>
      </Sheet>
      <Breadcrumb className="hidden md:flex">
        <BreadcrumbList>
          {items.map((item, index) => {
            const isLast = index === items.length - 1

            return (
              <Fragment key={`${item.label}-${index}`}>
                <BreadcrumbItem>
                  {isLast || !item.href ? (
                    <BreadcrumbPage>{item.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={item.href}>{item.label}</Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {index < items.length - 1 ? <BreadcrumbSeparator /> : null}
              </Fragment>
            )
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="relative ml-auto flex-1 md:grow-0">
        <Link to="/search" className="block">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search recipes..."
            className="w-full rounded-lg bg-background pl-8 md:w-[200px] lg:w-[336px] cursor-pointer"
            readOnly
          />
        </Link>
      </div>
      <NotificationBell />
      <UserNav />
    </header>
  )
}
